import classNames from "classnames"
import React, { useEffect, useRef, useState } from "react"

import { usePageLayoutState } from "@utils/layout"

type Props = {
  children: React.ReactNode
  isVisible?: boolean
  noPadding?: boolean
  showOnDesktop?: boolean
}

export const STICKY_PORTAL_ID = "sticky-portal"

const StickyContainer = ({
  children,
  isVisible,
  noPadding,
  showOnDesktop,
}: Props) => {
  const portalRef = useRef<HTMLDivElement>(null)
  const [hasPortalContents, setHasPortalContents] = useState(false)
  const { calculateStickyFooterHeight } = usePageLayoutState()

  useEffect(() => {
    const portalElement = portalRef.current

    const checkContents = () => {
      const hasContents = (portalElement?.children.length ?? 0) > 0
      setHasPortalContents(hasContents)
    }

    checkContents()

    if (portalElement) {
      const observer = new MutationObserver(checkContents)
      observer.observe(portalElement, { childList: true, subtree: true })

      return () => {
        observer.disconnect()
        calculateStickyFooterHeight(-75)
      }
    }
  }, [])

  useEffect(() => {
    calculateStickyFooterHeight(isVisible ? 75 : -75)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible])

  return (
    <div
      data-testid="sticky-container"
      className={classNames(
        { "sm:hidden": !showOnDesktop },
        isVisible
          ? "translate-y-0"
          : hasPortalContents
            ? "translate-y-[50%]"
            : "translate-y-full",
        "fixed bottom-0 left-0 z-30 h-min w-full bg-white transition-transform duration-300"
      )}
    >
      <div ref={portalRef} id={STICKY_PORTAL_ID} />
      <div
        className={classNames({
          "col-span-full flex items-center px-20 py-[calc(10px+env(safe-area-inset-bottom,0px))]":
            !noPadding,
        })}
      >
        {children}
      </div>
    </div>
  )
}

export default StickyContainer
